import React, { useState, useEffect } from "react";
import { getFileDownloadUrl } from "@/lib/files.api";

import "./index.scss";

export interface FileImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** API'den signed URL alınacak file ID */
  fileId?: string | null;
  /** fileId yoksa veya hata durumunda kullanılacak doğrudan URL */
  fallbackSrc?: string | null;
  /** Loading/error durumunda placeholder (örn. "Resim yüklenemedi") */
  placeholder?: React.ReactNode;
  /** Placeholder göstermek için object-fit */
  objectFit?: "cover" | "contain" | "fill" | "none";
}

/**
 * fileId ile img kullanımını basitleştiren component.
 * GET /api/client/files/:fileId/download → signed URL → img src
 *
 * @example
 * <FileImage fileId={coverImageFileID} alt="Kapak" className="cover" />
 * <FileImage fileId={id} alt="Profil" fallbackSrc="/default-avatar.png" />
 */
const FileImage: React.FC<FileImageProps> = ({
  fileId,
  fallbackSrc,
  placeholder,
  objectFit = "cover",
  alt,
  className = "",
  style,
  ...rest
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (!fileId) {
      setSrc(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setSrc(null);

    getFileDownloadUrl(fileId)
      .then((url) => {
        if (!cancelled) {
          setSrc(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSrc(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileId]);

  const effectiveSrc = src ?? fallbackSrc ?? null;
  const showPlaceholder = isLoading || !effectiveSrc;

  if (showPlaceholder) {
    return (
      <div
        className={`file-image file-image--placeholder ${className}`}
        style={{ ...style, objectFit }}
        data-placeholder
        aria-label={alt}
      >
        {placeholder ?? (
          <span className="file-image__placeholder-icon" aria-hidden>
            •
          </span>
        )}
      </div>
    );
  }

  return (
    <img
      {...rest}
      src={effectiveSrc!}
      alt={alt ?? ""}
      className={`file-image ${className}`}
      style={{ ...style, objectFit }}
    />
  );
};

export default FileImage;
